<template>
  <article class="card scroll-stagger">
    <div class="media">
      <img :alt="space.space_name"
           :src="picUrl + space.photo_url"
           loading="lazy"
           referrerpolicy="no-referrer"
           @error="onImgError" />
    </div>
    <div class="body">
      <div class="title">{{ space.space_name }}</div>
      <div class="meta">{{ fullAddress }}</div>
      <div class="row"><span class="badge">{{ capacityText }}</span></div>
      <div class="row">
        <span v-if="cheapestPrice && cheapestUnit"
              class="pill price">{{ cheapestPrice }} /{{ cheapestUnit }}</span>
      </div>
      <div class="cta">
        <a :href="spaceDetailHref" target="_blank" rel="noopener">空間詳情</a>
      </div>
    </div>
  </article>
</template>

<script setup>
import {computed} from 'vue'
import {parseFormatCheapestRentPrice, parseFormatCheapestRentType} from '~/utils/newPriceTool.ts'

const picUrl = import.meta.env.VITE_APP_BASE_IMG_URL

const props = defineProps({
  space: { type: Object, required: true }
})

const fullAddress = computed(() => {
  const city = props.space.city || ''
  const region = props.space.region || ''
  const address = props.space.address || ''
  return `${city}${region}${address}`
})

const capacityText = computed(() => `${props.space.number_of_people_max || 0}人`)

const cheapestPrice = computed(() => {
  const groups = props.space?.rent?.rent_groups
  if (!groups || (Array.isArray(groups) && groups.length === 0)) return ''
  try {
    return parseFormatCheapestRentPrice(groups)
  } catch (_) {
    return ''
  }
})
const cheapestUnit = computed(() => {
  const groups = props.space?.rent?.rent_groups
  if (!groups || (Array.isArray(groups) && groups.length === 0)) return ''
  try {
    return parseFormatCheapestRentType(groups)
  } catch (_) {
    return ''
  }
})

const spaceDetailHref = computed(() => `/space/${props.space.space_id}`)

function onImgError(e) {
  const target = e.target
  target.onerror = null
  target.src = 'https://picsum.photos/1024/683?random=913'
}
</script>

<style scoped lang="scss">
/* 單列大卡樣式，參考 HTML 版本 */
.card {
  position: relative;
  display: grid;
  grid-template-columns: minmax(320px, 1.05fr) minmax(300px, 0.95fr);
  gap: var(--s6, 28px);
  align-items: center;
  padding: var(--s6, 28px);
  border-radius: var(--radius, 8px);
  background: var(--bg, #ffffff);
  border: 1px solid var(--border-light, #f0f0f0);
  box-shadow: var(--shadow, 0 4px 20px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04));
  transition: all 0.4s ease;
}

.card:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-hover, 0 8px 30px rgba(0, 0, 0, 0.12), 0 4px 12px rgba(0, 0, 0, 0.06));
  border-color: var(--primary, #c9a876);
}

.card::before {
  content: "";
  position: absolute;
  inset: -1px auto -1px 0;
  width: 4px;
  border-radius: 4px 0 0 4px;
  background: var(--primary, #c9a876);
  opacity: 0.8;
}

.media {
  min-height: 320px;
  border-radius: var(--radius, 8px);
  overflow: hidden;
  background: var(--surface, #fafafa);
  border: 1px solid var(--border-light, #f0f0f0);
  transition: all 0.3s ease;
}

.media:hover {
  transform: scale(1.02);
  border-color: var(--primary, #c9a876);
}

.media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.body {
  display: flex;
  flex-direction: column;
  gap: var(--s4, 16px);
}

.title {
  font-weight: 700;
  letter-spacing: 0.1px;
  color: var(--text, #2c2c2c);
  font-size: 1.2rem;
}

.meta {
  font-size: 0.95rem;
  color: var(--text-muted, #666666);
  line-height: 1.5;
}

.row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  margin: var(--s3, 12px) 0;
}

.badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.88rem;
  padding: 8px 12px;
  border-radius: var(--radius, 8px);
  background: rgba(201, 168, 118, 0.1);
  color: var(--primary, #c9a876);
  border: 1px solid rgba(201, 168, 118, 0.25);
  font-weight: 600;
}

.pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0.6rem 0.9rem;
  border-radius: var(--radius, 8px);
  background: var(--surface, #fafafa);
  color: var(--text, #2c2c2c);
  border: 1px solid var(--border, #e5e5e5);
  font-weight: 600;
  transition: all 0.3s ease;
}

.pill:hover {
  border-color: var(--primary, #c9a876);
  background: rgba(201, 168, 118, 0.05);
}

.pill.price {
  background: var(--primary, #c9a876);
  color: #fff;
  border: 0;
  font-weight: 700;
  box-shadow: var(--shadow, 0 4px 20px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04));
}

.pill.price:hover {
  transform: scale(1.05);
  box-shadow: var(--shadow-hover, 0 8px 30px rgba(0, 0, 0, 0.12), 0 4px 12px rgba(0, 0, 0, 0.06));
}

.cta {
  margin-top: var(--s4, 16px);
}

.cta a {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 0.8rem 1.2rem;
  border-radius: var(--radius, 8px);
  background: var(--primary, #c9a876);
  color: #fff;
  font-weight: 700;
  transition: all 0.3s ease;
  box-shadow: var(--shadow, 0 4px 20px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04));
  text-decoration: none;
}

.cta a:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-hover, 0 8px 30px rgba(0, 0, 0, 0.12), 0 4px 12px rgba(0, 0, 0, 0.06));
}

.cta a::after {
  content: "→";
  margin-left: 4px;
  font-weight: bold;
}

/* 響應式設計 */
@media (max-width: 900px) {
  .card {
    grid-template-columns: 1fr;
    gap: var(--s6, 28px);
  }
  
  .media {
    min-height: 250px;
  }
}
</style> 